<template>
  <div>
    <core-toolbar
      v-if="group"
      :group="group"
      :home-path="$route.path"
      @navigate="tab = $event"
      @logout="logout" />
    <core-drawer
      v-if="group"
      :navigation="navigationLinks"
      :active-view="tab"
      :home-path="$route.path"
      @navigate="tab = $event" />
    <v-content>
      <div id="core-view">
        <v-container
          fluid
          grid-list-xl
          class="group-dashboard">
          <v-progress-linear
            v-if="initialLoading"
            indeterminate
            color="primary" />
          <v-alert
            :value="!!error"
            type="error">{{ error }}</v-alert>
          <v-card
            v-if="!initialLoading && !group && !error"
            class="pa-4">
            <h2>Open your access link</h2>
            <p class="mt-2">Use the dashboard link provided by HSP to view suppliers and tasting activity.</p>
          </v-card>

          <template v-if="group">
            <v-card
              v-show="tab !== 2"
              class="pa-4 rounded-lg mb-4">
              <section class="group-filters">
                <v-select
                  v-model="supplier"
                  :items="supplierOptions"
                  item-text="label"
                  item-value="code"
                  label="Supplier"
                  hide-details />
                <v-autocomplete
                  v-model="brand"
                  :items="filterOptions.brands"
                  item-text="label"
                  item-value="value"
                  label="Brand"
                  clearable
                  hide-details
                  @change="brandChanged" />
                <v-autocomplete
                  v-model="product"
                  :items="productOptions"
                  item-text="label"
                  item-value="value"
                  label="Product / UPC"
                  clearable
                  hide-details
                  @change="loadActive" />
                <v-autocomplete
                  v-model="workOrder"
                  :items="filterOptions.work_orders"
                  item-text="label"
                  item-value="value"
                  label="Work order"
                  clearable
                  hide-details
                  @change="loadActive" />
                <v-autocomplete
                  v-model="location"
                  :items="filterOptions.stores"
                  item-text="label"
                  item-value="value"
                  label="Store / location"
                  clearable
                  hide-details
                  @change="loadActive" />
                <v-menu
                  v-model="startMenu"
                  :close-on-content-click="false"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px">
                  <v-text-field
                    slot="activator"
                    :value="displayDate(rangeStart)"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    hide-details />
                  <v-date-picker
                    v-model="rangeStart"
                    no-title
                    min="2020-01-01"
                    @input="startMenu = false" />
                </v-menu>
                <v-menu
                  v-model="endMenu"
                  :close-on-content-click="false"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px">
                  <v-text-field
                    slot="activator"
                    :value="displayDate(rangeEnd)"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    hide-details />
                  <v-date-picker
                    v-model="rangeEnd"
                    no-title
                    min="2020-01-01"
                    @input="endMenu = false" />
                </v-menu>
                <v-btn
                  :loading="loading"
                  color="primary"
                  @click="loadActive">Apply dates</v-btn>
                <v-btn
                  flat
                  @click="clearFilters">Clear filters</v-btn>
                <v-btn
                  :disabled="loading || !hasData"
                  flat
                  @click="download"><v-icon left>mdi-download</v-icon>Download CSV</v-btn>
              </section>
            </v-card>

            <section v-show="tab === 0">
              <v-layout
                row
                wrap
                class="metric-row">
                <v-flex
                  v-for="metric in visibleMetrics"
                  :key="metric.key"
                  xs12
                  sm6
                  md6
                  lg4
                  class="pa-2 metric-card">
                  <material-stats-card
                    :value="formatMetric(report.totals[metric.key], metric.key)"
                    :title="metric.label"
                    :icon="metric.icon"
                    color="royal" />
                  <v-tooltip
                    v-if="metric.help"
                    top>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="metric-help"
                        small
                        v-on="on">mdi-information</v-icon>
                    </template>
                    <span>{{ metric.help }}</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
              <v-card class="mt-3">
                <v-card-title><h2 class="section-title">Supplier performance</h2></v-card-title>
                <v-data-table
                  :headers="supplierHeaders"
                  :items="report.suppliers"
                  :loading="loading"
                  :rows-per-page-items="[15, 50]"
                  item-key="client_code"
                  no-data-text="No suppliers are available for this selection.">
                  <template
                    slot="items"
                    slot-scope="props">
                    <td>{{ supplierName(props.item.client_name) }}</td>
                    <td class="text-xs-right">{{ number(props.item.events) }}</td>
                    <td class="text-xs-right">{{ number(props.item.samples) }}</td>
                    <td class="text-xs-right">{{ number(props.item.units) }}</td>
                    <td class="text-xs-right">{{ money(props.item.sales) }}</td>
                  </template>
                </v-data-table>
              </v-card>
              <v-card class="mt-4">
                <v-card-title><h2 class="section-title">Product performance</h2></v-card-title>
                <v-data-table
                  :headers="productHeaders"
                  :items="report.products"
                  :loading="loading"
                  :rows-per-page-items="[10, 25, 50]"
                  item-key="product_id"
                  no-data-text="No product results match these filters.">
                  <template
                    slot="items"
                    slot-scope="props">
                    <td>{{ props.item.brand }}</td>
                    <td>{{ props.item.product }}</td>
                    <td>{{ props.item.upc || '-' }}</td>
                    <td class="text-xs-right">{{ number(props.item.events) }}</td>
                    <td class="text-xs-right">{{ number(props.item.samples) }}</td>
                    <td class="text-xs-right">{{ number(props.item.units) }}</td>
                    <td class="text-xs-right">{{ money(props.item.sales) }}</td>
                  </template>
                </v-data-table>
              </v-card>
            </section>

            <section v-show="tab === 3">
              <v-card>
                <v-card-title class="recap-controls">
                  <h2 class="section-title">Event Details</h2>
                  <span aria-live="polite">{{ loading ? 'Loading events...' : recapRange }}</span>
                  <v-spacer />
                  <v-select
                    v-model="recapPagination.rowsPerPage"
                    :items="recapPageSizes"
                    :disabled="loading"
                    label="Events per page"
                    class="recap-page-size"
                    hide-details
                    @change="recapPagination.page = 1" />
                  <v-pagination
                    v-if="recapPageCount > 1"
                    v-model="recapPagination.page"
                    :length="recapPageCount"
                    :total-visible="5"
                    :disabled="loading"
                    aria-label="Event details pages" />
                </v-card-title>
                <v-data-table
                  :key="loadVersion"
                  :headers="recapHeaders"
                  :items="report.recaps"
                  :loading="loading"
                  :pagination.sync="recapPagination"
                  :rows-per-page-items="recapPageSizes"
                  item-key="timesheet_id"
                  no-data-text="No approved tasting results in this date range.">
                  <template
                    slot="items"
                    slot-scope="props">
                    <tr @click="!loading && (props.expanded = !props.expanded)">
                      <td class="recap-date">
                        <v-btn
                          :disabled="loading"
                          :aria-expanded="props.expanded ? 'true' : 'false'"
                          :aria-controls="'event-details-' + props.item.timesheet_id"
                          :aria-label="(props.expanded ? 'Collapse' : 'Expand') + ' event ' + displayDate(props.item.timesheet_date) + ' at store ' + props.item.warehouse_id"
                          flat
                          icon
                          small
                          @click.stop="props.expanded = !props.expanded">
                          <v-icon>{{ props.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        </v-btn>
                        {{ props.item.timesheet_date }}
                      </td>
                      <td>{{ supplierName(props.item.client_name) }}</td>
                      <td>{{ props.item.work_order || '-' }}</td>
                      <td>#{{ props.item.warehouse_id }} · {{ props.item.city }}</td>
                      <td class="text-xs-right">{{ number(props.item.samples) }}</td>
                      <td class="text-xs-right">{{ number(props.item.units) }}</td>
                      <td class="text-xs-right">{{ money(props.item.sales) }}</td>
                      <td>
                        <v-btn
                          :disabled="loading || pdfLoading"
                          :aria-label="'Download PDF for ' + props.item.timesheet_date + ' store ' + props.item.warehouse_id"
                          flat
                          icon
                          @click.stop="downloadEvent(props.item)"><v-icon>mdi-cloud-download</v-icon></v-btn>
                      </td>
                    </tr>
                  </template>
                  <template
                    slot="expand"
                    slot-scope="props">
                    <div :id="'event-details-' + props.item.timesheet_id">
                      <event-details
                        :recap="props.item"
                        show-supplier />
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </section>

            <section v-show="tab === 1">
              <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary" />
              <div class="calendar-scroll">
                <table
                  :style="{ minWidth: (180 + days.length * 160) + 'px' }"
                  class="group-calendar">
                  <thead><tr><th class="store-column">Store</th><th
                    v-for="day in days"
                    :key="day.date"
                    :class="{ monday: day.monday }">{{ day.label }}<small>{{ day.weekday }}</small></th></tr></thead>
                  <tbody>
                    <tr
                      v-for="store in calendar.stores"
                      :key="store.costco_id">
                      <th class="store-column"><strong>#{{ store.warehouse_id }}</strong><span>{{ store.city }}</span></th>
                      <td
                        v-for="day in days"
                        :key="day.date"
                        :class="{ monday: day.monday }">
                        <button
                          v-for="event in cellEvents(store.costco_id, day.date)"
                          :key="event.schedule_id"
                          :class="{ pending: event.pending }"
                          class="calendar-event"
                          @click="selectedEvent = event">
                          {{ supplierName(event.client_name) }}<small>{{ eventTime(event) }}</small><small v-if="event.pending">Pending approval</small><small v-else-if="event.boosted">Boosted</small>
                        </button>
                        <span
                          v-for="(period, index) in cellBusy(store.costco_id, day.date)"
                          :key="'busy-' + index"
                          class="reserved">Busy / Not available<small>{{ eventTime(period) }}</small></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p
                v-if="!loading && !calendar.stores.length"
                class="pa-3">No stores are available for this dashboard.</p>
            </section>

            <section v-show="tab === 4">
              <material-card
                :text="`Email ${contactEmail} with updates or questions. Events subject to change.`"
                color="secondary"
                title="Schedule">
                <div class="pa-3">
                  <v-btn
                    :loading="sharing"
                    :disabled="loading || !calendar.date_start"
                    color="primary"
                    @click="copyScheduleLink">
                    <v-icon left>mdi-content-copy</v-icon>Copy Schedule Link
                  </v-btn>
                  <span role="status">{{ shareMessage }}</span>
                  <v-text-field
                    v-if="shareUrl"
                    :value="shareUrl"
                    label="Shared schedule link"
                    readonly
                    @focus="$event.target.select()" />
                </div>
                <group-schedule-table
                  :events="scheduleEvents"
                  :loading="loading"
                  :store-label="group.program.store_label"
                  :state="group.scope.state" />
              </material-card>
            </section>

            <section
              v-if="group.can_request"
              v-show="tab === 2">
              <section
                v-if="group.program.notice_days"
                aria-labelledby="permit-notice-title"
                class="permit-notice">
                <h2 id="permit-notice-title">Arizona tastings require 10 calendar days' notice - DLLC permits</h2>
                <p>HSP needs this time to coordinate DLLC permits. Events within the next 10 days cannot be requested.</p>
                <strong>Earliest available date: {{ displayDate(group.program.earliest_date) }}</strong>
              </section>

              <v-card class="pa-3 mt-3 mb-3">
                <v-select
                  v-model="requestSupplier"
                  :items="group.suppliers"
                  :item-text="item => supplierName(item.client_name)"
                  :disabled="requestLocked"
                  item-value="client_code"
                  label="Supplier to schedule"
                  hide-details />
                <p
                  v-if="requestLocked"
                  class="mt-2 mb-0">Finish or clear the current request before selecting another supplier.</p>
              </v-card>
              <event-request
                v-if="requestContext"
                :key="requestSupplier"
                :group-context="requestContext"
                @show-calendar="tab = 1"
                @draft-change="requestLocked = $event"
                @request-complete="requestCompleted" />
              <p
                v-else
                class="pa-3">Select a supplier to start a tasting request.</p>
            </section>
          </template>

          <v-dialog
            :value="!!selectedEvent"
            max-width="520"
            @input="value => {
              if (!value) selectedEvent = null
          }">
            <v-card v-if="selectedEvent">
              <v-card-title class="headline">{{ supplierName(selectedEvent.client_name) }}</v-card-title>
              <v-card-text>
                <p>{{ selectedEvent.schedule_date }} · {{ time(selectedEvent.time_start) }} - {{ time(selectedEvent.time_end) }}</p>
                <p>#{{ selectedEvent.warehouse_id }} {{ selectedEvent.storename }}<br>{{ selectedEvent.address }}, {{ selectedEvent.city }}</p>
                <p><strong>Work order:</strong> {{ selectedEvent.work_order || '-' }}</p>
                <strong>Products</strong><p>{{ productNames(selectedEvent.products) || 'No products listed' }}</p>
                <template v-if="selectedEvent.substitutions.length"><strong>Substitutions</strong><p>{{ productNames(selectedEvent.substitutions) }}</p></template>
                <p v-if="selectedEvent.pending">Pending HSP approval</p>
              </v-card-text>
              <v-card-actions><v-spacer /><v-btn
                flat
                @click="selectedEvent = null">Close</v-btn></v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>
      </div>
      <core-footer />
    </v-content>
  </div>
</template>

<script>
import EventDetails from '../components/EventDetails'
import GroupScheduleTable from '../components/GroupScheduleTable'
import EventRequest from './EventRequest'
import {
  groupGet, groupPost, downloadFile, scheduleCsv, groupToken, clearGroupToken, groupAccessState, calendarDays, downloadCsv,
  supplierName as displaySupplierName, GROUP_CONTACT_EMAIL
} from '../services/group.service'

function arizonaToday () {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit'
  })
}

function emptyReport () {
  return { totals: {}, suppliers: [], recaps: [], products: [] }
}

function emptyCalendar () {
  return { stores: [], events: {}, busy: [] }
}

function emptyFilters () {
  return { brands: [], products: [], work_orders: [], stores: [] }
}

const filterFields = { brands: 'brand', products: 'product', work_orders: 'workOrder', stores: 'location' }

export default {
  components: {
    EventDetails,
    GroupScheduleTable,
    EventRequest
  },
  data () {
    const today = arizonaToday()
    const monthEnd = new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0)).toISOString().slice(0, 10)
    return {
      group: null,
      token: '',
      tab: 0,
      startMenu: false,
      endMenu: false,
      supplier: '',
      brand: '',
      product: '',
      workOrder: '',
      location: '',
      filterOptions: emptyFilters(),
      requestSupplier: '',
      requestLocked: false,
      initialLoading: true,
      loading: false,
      pendingReportSelection: null,
      error: '',
      loadVersion: 0,
      initializationVersion: 0,
      shareVersion: 0,
      sharing: false,
      shareMessage: '',
      shareUrl: '',
      report: emptyReport(),
      calendar: emptyCalendar(),
      reportStart: today.slice(0, 4) + '-01-01',
      reportEnd: today,
      calendarStart: today.slice(0, 7) + '-01',
      calendarEnd: monthEnd,
      selectedEvent: null,
      recapPageSizes: [10, 25, 50, { text: 'All', value: -1 }],
      recapPagination: { page: 1, rowsPerPage: -1, sortBy: 'timesheet_date', descending: true },
      pdfLoading: false,
      metrics: [
        { key: 'events', label: 'Sampling Events', icon: 'mdi-glass-wine' },
        { key: 'units', label: 'Units Sold', icon: 'mdi-bottle-wine' },
        { key: 'customers', label: 'Shoppers Engaged', icon: 'mdi-account-group' },
        { key: 'sales', label: 'Total Sales', icon: 'mdi-currency-usd' },
        { key: 'packs', label: 'Packs Sold', icon: 'mdi-package-variant' },
        { key: 'samples', label: 'Samples Given', icon: 'mdi-cup-water' },
        { key: 'adequate_stock', label: 'Adequate Stock', icon: 'mdi-clipboard-check' },
        { key: 'impressions', label: 'Impressions', icon: 'mdi-eye', help: 'Not available for this store selection. Advertising impressions are recorded for supplier campaigns, not individual stores.' },
        { key: 'acquisition_cost', label: 'Customer Acquisition Cost', icon: 'mdi-account-check', help: 'Calculated as (Total Billed - Total Sales × 1.5) / Samples Given for the selected events.' }
      ],
      supplierHeaders: [
        {
          text: 'Supplier', value: 'client_name'
        },
        {
          text: 'Tastings', value: 'events', align: 'right'
        },
        {
          text: 'Samples', value: 'samples', align: 'right'
        },
        {
          text: 'Units sold', value: 'units', align: 'right'
        },
        {
          text: 'Sales', value: 'sales', align: 'right'
        }
      ],
      productHeaders: [
        { text: 'Brand', value: 'brand' },
        { text: 'Product', value: 'product' },
        { text: 'UPC', value: 'upc' },
        { text: 'Tastings', value: 'events', align: 'right' },
        { text: 'Samples', value: 'samples', align: 'right' },
        { text: 'Units sold', value: 'units', align: 'right' },
        { text: 'Sales', value: 'sales', align: 'right' }
      ],
      recapHeaders: [
        {
          text: 'Date', value: 'timesheet_date'
        },
        {
          text: 'Supplier', value: 'client_name'
        },
        {
          text: 'Work order', value: 'work_order'
        },
        {
          text: 'Store', value: 'city'
        },
        {
          text: 'Samples', value: 'samples', align: 'right'
        },
        {
          text: 'Units', value: 'units', align: 'right'
        },
        {
          text: 'Sales', value: 'sales', align: 'right'
        },
        {
          text: 'PDF', sortable: false
        }
      ]
    }
  },
  computed: {
    contactEmail: () => GROUP_CONTACT_EMAIL,
    recapPageCount () {
      if (this.recapPagination.rowsPerPage === -1) return 1
      return Math.max(1, Math.ceil(this.report.recaps.length / this.recapPagination.rowsPerPage))
    },
    recapRange () {
      const total = this.report.recaps.length
      if (this.recapPagination.rowsPerPage === -1) return `All ${this.number(total)} events`
      const first = total ? (this.recapPagination.page - 1) * this.recapPagination.rowsPerPage + 1 : 0
      const last = Math.min(this.recapPagination.page * this.recapPagination.rowsPerPage, total)
      return `${first}-${last} of ${this.number(total)} events`
    },
    visibleMetrics () {
      const includesPacks = this.report.recaps.some(recap => recap.includes_packs)
      return this.metrics.filter(metric => metric.key !== 'packs' || includesPacks)
    },
    accessRevision () {
      return groupAccessState.revision
    },
    navigationLinks () {
      const links = [
        { tab: 0, text: 'Analytics', icon: 'mdi-finance' },
        { tab: 3, text: 'Event Details', icon: 'mdi-clipboard-check' },
        { tab: 1, text: 'Calendar', icon: 'mdi-calendar-month' },
        { tab: 4, text: 'Schedule', icon: 'mdi-format-list-bulleted' }
      ]
      if (this.group && this.group.can_request) links.push({ tab: 2, text: 'Event Request', icon: 'mdi-clipboard-text' })
      return links
    },
    code () {
      return this.$route.params.group_code
    },
    supplierOptions () {
      return [{ code: '', label: 'All suppliers' }].concat(this.group.suppliers.map(item => ({ code: item.client_code, label: this.supplierName(item.client_name) })))
    },
    productOptions () {
      return this.filterOptions.products.filter(item => !this.brand || item.brand_id === this.brand)
    },
    rangeStart: {
      get () {
        return [1, 4].includes(this.tab) ? this.calendarStart : this.reportStart
      },
      set (value) {
        if ([1, 4].includes(this.tab)) this.calendarStart = value
        else this.reportStart = value
      }
    },
    rangeEnd: {
      get () {
        return [1, 4].includes(this.tab) ? this.calendarEnd : this.reportEnd
      },
      set (value) {
        if ([1, 4].includes(this.tab)) this.calendarEnd = value
        else this.reportEnd = value
      }
    },
    days () {
      return calendarDays(this.calendar.date_start, this.calendar.date_end)
    },
    eventMap () {
      const result = {}
      Object.keys(this.calendar.events).forEach(date => {
        this.calendar.events[date].forEach(event => {
          const key = event.costco_id + ':' + date
          if (!result[key]) result[key] = []
          result[key].push(event)
        })
      })
      return result
    },
    scheduleEvents () {
      return Object.keys(this.calendar.events).sort().reduce((events, date) => events.concat(this.calendar.events[date]), [])
    },
    hasData () {
      return [1, 4].includes(this.tab) ? !!this.calendar.date_start : !!this.report.date_start
    },
    requestContext () {
      const selected = this.group && this.group.suppliers.find(item => item.client_code === this.requestSupplier)
      return selected ? {
        code: this.code, token: this.token, program: this.group.program, supplier: selected
      } : null
    }
  },
  watch: {
    accessRevision () {
      this.initialize()
    },
    supplier () {
      this.resetDetailFilters()
      this.loadActive()
    },
    tab (tab, previousTab) {
      this.startMenu = false
      this.endMenu = false
      if (window.innerWidth < 991) this.$store.commit('app/setDrawer', false)
      if ([0, 3].includes(tab) && [0, 3].includes(previousTab)) {
        const selection = this.selectionFor(false)
        const loaded = this.loading ? this.pendingReportSelection : this.report.selection
        if (loaded && Object.keys(selection).every(key => selection[key] === loaded[key])) return
      }
      this.loadActive()
    },
    '$route.params.group_code' () {
      this.initialize()
    }
  },
  created () {
    this.initialize()
  },
  beforeDestroy () {
    this.initializationVersion++
    this.loadVersion++
    this.invalidateShare()
  },
  methods: {
    eventTime (event) {
      if (!event.time_start || !event.time_end || event.time_end <= event.time_start) return 'Time unavailable - contact HSP'
      const format = value => {
        const [hour, minute] = value.split(':')
        return `${Number(hour) % 12 || 12}:${minute} ${Number(hour) < 12 ? 'AM' : 'PM'}`
      }
      return `${format(event.time_start)} - ${format(event.time_end)}`
    },
    cellBusy (store, date) {
      const visible = this.cellEvents(store, date)
      return this.calendar.busy.filter(period => String(period.costco_id) === String(store) && period.schedule_date === date &&
        !visible.some(event => event.time_start === period.time_start && event.time_end === period.time_end))
    },
    async copyScheduleLink () {
      const version = ++this.shareVersion
      this.sharing = true
      this.shareMessage = ''
      this.shareUrl = ''
      try {
        const response = await groupPost(this.$store.state.apiRoot, this.code, this.token, '/share-schedule', this.calendar.selection)
        if (version !== this.shareVersion) return
        this.shareUrl = `${window.location.origin}/shared-schedule?token=${response.data.token}`
        try {
          await navigator.clipboard.writeText(this.shareUrl)
          if (version === this.shareVersion) this.shareMessage = 'Schedule link copied!'
        } catch (error) {
          if (version === this.shareVersion) this.shareMessage = 'Select and copy the link below.'
        }
      } catch (error) {
        if (version === this.shareVersion) this.shareMessage = this.errorMessage(error)
      } finally {
        if (version === this.shareVersion) this.sharing = false
      }
    },
    invalidateShare () {
      this.shareVersion++
      this.sharing = false
      this.shareUrl = ''
      this.shareMessage = ''
    },
    logout () {
      clearGroupToken(this.code)
      this.tab = 0
      this.initialize()
    },
    displayDate (date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },
    async initialize () {
      const initialization = ++this.initializationVersion
      this.loadVersion++
      this.group = null
      this.tab = 0
      this.loading = false
      this.pendingReportSelection = null
      this.invalidateShare()
      this.report = emptyReport()
      this.calendar = emptyCalendar()
      this.supplier = ''
      this.resetDetailFilters()
      this.selectedEvent = null
      this.recapPagination.page = 1
      this.requestSupplier = ''
      this.requestLocked = false
      this.error = ''
      this.token = groupToken(this.code)
      this.initialLoading = true
      try {
        if (!this.token) return
        await this.$store.dispatch('get_config')
        if (initialization !== this.initializationVersion) return
        const response = await groupGet(this.$store.state.apiRoot, this.code, this.token)
        if (initialization !== this.initializationVersion) return
        this.group = response.data
        await this.loadActive()
      } catch (error) {
        if (initialization === this.initializationVersion) this.error = this.errorMessage(error)
      } finally {
        if (initialization === this.initializationVersion) this.initialLoading = false
      }
    },
    selectionFor (calendar) {
      return {
        date_start: calendar ? this.calendarStart : this.reportStart,
        date_end: calendar ? this.calendarEnd : this.reportEnd,
        supplier: this.supplier,
        brand: this.brand || '',
        product: this.product || '',
        work_order: this.workOrder || '',
        store: this.location || ''
      }
    },
    async loadActive () {
      const version = ++this.loadVersion
      this.pendingReportSelection = null
      this.invalidateShare()
      if (!this.group || this.tab === 2) {
        this.loading = false
        return
      }
      const calendar = [1, 4].includes(this.tab)
      this.loading = true
      this.error = ''
      this.selectedEvent = null
      const selection = this.selectionFor(calendar)
      if (!calendar) this.pendingReportSelection = selection
      try {
        const response = await groupGet(this.$store.state.apiRoot, this.code, this.token, calendar ? '/calendar' : '/overview', selection)
        if (version !== this.loadVersion) return
        this.updateFilterOptions(response.data.filters || emptyFilters())
        response.data.selection = selection
        if (calendar) this.calendar = response.data
        else {
          this.report = response.data
          this.recapPagination.page = 1
        }
      } catch (error) {
        if (version !== this.loadVersion) return
        this.error = this.errorMessage(error)
        if (calendar) this.calendar = emptyCalendar()
        else this.report = emptyReport()
        if (error.response && [401, 403].indexOf(error.response.status) !== -1) this.group = null
      } finally {
        if (version === this.loadVersion) {
          this.loading = false
          this.pendingReportSelection = null
        }
      }
    },
    resetDetailFilters () {
      this.brand = ''
      this.product = ''
      this.workOrder = ''
      this.location = ''
      this.filterOptions = emptyFilters()
    },
    clearFilters () {
      const supplierChanged = !!this.supplier
      this.supplier = ''
      this.resetDetailFilters()
      if (!supplierChanged) this.loadActive()
    },
    brandChanged () {
      this.product = ''
      this.loadActive()
    },
    updateFilterOptions (incoming) {
      Object.keys(filterFields).forEach(key => {
        const selected = this[filterFields[key]]
        const previous = this.filterOptions[key].find(item => item.value === selected)
        if (previous && !incoming[key].some(item => item.value === selected)) incoming[key].push(previous)
      })
      this.filterOptions = incoming
    },
    productNames (products) {
      return (products || []).map(item => `${item.brand} ${item.product}`.trim()).join('; ')
    },
    exportSelection (data) {
      const selection = data.selection || {}
      const row = ['Filters', 'Supplier: ' + (selection.supplier || 'All')]
      const parameters = { brands: 'brand', products: 'product', work_orders: 'work_order', stores: 'store' }
      Object.keys(parameters).forEach(key => {
        const value = selection[parameters[key]]
        if (value) {
          const option = (data.filters[key] || []).find(item => item.value === value)
          row.push(parameters[key] + ': ' + (option ? option.label : value))
        }
      })
      return [['From', data.date_start, 'Through', data.date_end], row, []]
    },
    errorMessage (error) {
      return (error.response && error.response.data && error.response.data.message) || 'The dashboard could not be loaded. Please try again.'
    },
    supplierName (name) {
      return displaySupplierName(name)
    },
    number (value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 1
      })
    },
    money (value) {
      return Number(value || 0).toLocaleString('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 2
      })
    },
    formatMetric (value, key) {
      if (value == null) return 'N/A'
      if (key === 'adequate_stock') return Number(value).toFixed(0) + '%'
      return ['sales', 'acquisition_cost'].includes(key) ? this.money(value) : this.number(value)
    },
    time (value) {
      if (!value) return ''
      const [hour, minute] = value.split(':')
      return `${Number(hour) % 12 || 12}:${minute} ${Number(hour) < 12 ? 'AM' : 'PM'}`
    },
    cellEvents (store, date) {
      return this.eventMap[store + ':' + date] || []
    },
    requestCompleted () {
      this.requestLocked = false
    },
    async downloadEvent (recap) {
      const version = this.loadVersion
      this.pdfLoading = true
      try {
        const response = await groupGet(this.$store.state.apiRoot, this.code, this.token, `/events/${recap.timesheet_id}/pdf`, this.report.selection || {}, 'blob')
        if (version !== this.loadVersion) return
        downloadFile(`Event Details - ${recap.timesheet_date} - ${recap.warehouse_id}.pdf`, response.data)
      } catch (error) {
        if (version === this.loadVersion) this.error = 'The event PDF could not be downloaded. Please reload and try again.'
      } finally {
        this.pdfLoading = false
      }
    },
    download () {
      if (this.tab === 4) {
        downloadCsv(`${this.code}-schedule-${this.calendar.date_start}.csv`, scheduleCsv(this.scheduleEvents, { storeLabel: this.group.program.store_label, state: this.group.scope.state }))
        return
      }
      if ([1, 4].includes(this.tab)) {
        const rows = this.exportSelection(this.calendar)
        rows.push(['Store', 'City'].concat(this.days.map(day => day.date)))
        this.calendar.stores.forEach(store => {
          rows.push(['#' + store.warehouse_id, store.city].concat(this.days.map(day => {
            const events = this.cellEvents(store.costco_id, day.date)
            return events.map(event => this.supplierName(event.client_name) + ' [' + (event.work_order || '-') + ']' + (event.pending ? ' (pending)' : '') + ' ' + this.eventTime(event))
              .concat(this.cellBusy(store.costco_id, day.date).map(period => 'Busy / Not available ' + this.eventTime(period))).join('; ')
          })))
        })
        rows.push([], ['Date', 'Supplier', 'Work order', 'Store', 'City', 'Start', 'End', 'Status', 'Products', 'Substitutions'])
        Object.keys(this.calendar.events).sort().forEach(date => this.calendar.events[date].forEach(event => {
          rows.push([date, this.supplierName(event.client_name), event.work_order, event.warehouse_id, event.city, this.time(event.time_start), this.time(event.time_end), event.pending ? 'Pending approval' : 'Published', this.productNames(event.products), this.productNames(event.substitutions)])
        }))
        downloadCsv(`${this.code}-calendar-${this.calendar.date_start}.csv`, rows)
      } else {
        const rows = this.exportSelection(this.report)
        rows.push(['Metric', 'Value'])
        this.metrics.forEach(metric => rows.push([metric.label, this.report.totals && this.report.totals[metric.key] != null ? this.report.totals[metric.key] : 'N/A']))
        rows.push([])
        rows.push(['Supplier', 'Tastings', 'Samples', 'Units sold', 'Retail sales'])
        this.report.suppliers.forEach(item => rows.push([this.supplierName(item.client_name), item.events, item.samples, item.units, item.sales]))
        rows.push([], ['Brand', 'Product', 'UPC', 'Tastings', 'Samples', 'Units sold', 'Retail sales'])
        this.report.products.forEach(item => rows.push([item.brand, item.product, item.upc, item.events, item.samples, item.units, item.sales]))
        rows.push([], ['Date', 'Supplier', 'Work order', 'Store', 'City', 'Samples Given', 'Units Sold', 'Total Sales', 'Comments', 'Shoppers Engaged', 'Adequate Product', 'Department Staffed', 'Checked In With', 'Where', 'Coupons', 'Start', 'End', 'Hours', 'Event Type'])
        this.report.recaps.forEach(item => rows.push([item.timesheet_date, this.supplierName(item.client_name), item.work_order, item.warehouse_id, item.city, item.samples, item.units, item.sales, item.comments, item.customers, item.adequate_product, item.department_staffed, item.check_in_name, item.conducted_where, item.coupons, item.time_start, item.time_end, item.hours, item.sub_name]))
        rows.push([], ['Date', 'Supplier', 'Work order', 'Store', 'City', 'Brand', 'Product', 'UPC', 'Samples', 'Units sold', 'Retail sales'])
        this.report.recaps.forEach(recap => recap.products.forEach(item => {
          rows.push([recap.timesheet_date, this.supplierName(recap.client_name), recap.work_order, recap.warehouse_id, recap.city, item.brand, item.product, item.upc, item.samples, item.units, item.sales])
        }))
        downloadCsv(`${this.code}-results-${this.report.date_start}.csv`, rows)
      }
    }
  }
}
</script>

<style scoped>
.group-dashboard .permit-notice {
  background: #fff;
  color: #333;
  border-left: 4px solid #10069f;
  border-radius: 4px;
  font-size: 16px;
  padding: 16px 20px;
  line-height: 1.5;
}
.group-dashboard .permit-notice h2 {
  color: #10069f;
  font-size: 20px !important;
  font-weight: 700 !important;
  line-height: 1.35;
  margin: 0;
}
.group-dashboard .permit-notice p {
  margin: 8px 0;
}
.group-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  align-items: end;
}
.metric-card {
  position: relative;
}
.metric-help {
  position: absolute;
  top: 12px;
  right: 12px;
}
.metric-row {
  margin: 0 -8px;
}
.section-title {
  font-size: 20px !important;
  font-weight: 500 !important;
}
.recap-controls {
  gap: 16px;
}
.recap-page-size {
  flex: 0 1 140px;
}
.recap-date {
  white-space: nowrap;
}
.calendar-scroll {
  max-height: 70vh;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
}
.group-calendar {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  width: 100%;
}
.group-calendar th, .group-calendar td {
  min-width: 160px;
  border-right: 1px solid #e3e6ee;
  border-bottom: 1px solid #e3e6ee;
  padding: 7px;
  vertical-align: top;
}
.group-calendar thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f5f5;
  color: #333;
  text-align: center;
}
.group-calendar small, .group-calendar span {
  display: block;
}
.group-calendar .store-column {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 1;
  min-width: 180px;
  width: 180px;
  text-align: left;
}
.group-calendar thead .store-column {
  z-index: 3;
}
.group-calendar .monday {
  border-left: 3px solid #b0b0b0;
}
.calendar-event {
  display: block;
  width: 100%;
  padding: 7px;
  margin-bottom: 3px;
  border-radius: 4px;
  background: #1565c0;
  color: #fff;
  text-align: left;
}
.calendar-event.pending {
  background: #fff2d7;
  color: #805300;
}
.reserved {
  color: #626773;
  padding: 7px;
  background: #edeff3;
  border-radius: 4px;
}
.recap-comments {
  white-space: pre-wrap;
  margin-top: 10px;
}
@media (max-width: 1264px) {
  .group-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 600px) {
  .group-filters {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
}
</style>
